import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, type HearingItem } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";

// バックエンドのデフォルトと同期（DB が null のときはこの値を表示・送信時にデフォルトと一致すれば null を送る）
const DEFAULT_SUBJECT_PREFIX = "[商談ヒアリング] ";
const DEFAULT_BODY_TEMPLATE = `以下の商談について、ヒアリング内容のご記入をお願いします。

【商談】{{dealTitle}}
【担当者】{{assigneeName}}
【終了日時】{{dateStr}}

--- 以下の項目にご記入の上、このメールに返信してください ---

{{hearingSection}}

よろしくお願いいたします。`;
const DEFAULT_REPORT_SYSTEM = `あなたは営業報告を整形するアシスタントです。
ユーザーから渡される生のヒアリングメモ（rawReport）を、以下の形式で整理した営業報告書に変換してください。
- 見出しと箇条書きを使う
- 不明な部分は「要確認」とし、事実と推測を分ける
- 日本語で出力`;
const DEFAULT_REPORT_USER_PREFIX =
  "以下の営業ヒアリングメモを、読みやすい営業報告書に整形してください。\n\n---\n";

export function SettingsPage() {
  const { company, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [subjectPrefix, setSubjectPrefix] = useState("");
  const [bodyTemplate, setBodyTemplate] = useState("");
  const [reportSystemPrompt, setReportSystemPrompt] = useState("");
  const [reportUserPrefix, setReportUserPrefix] = useState("");
  const [hearingItems, setHearingItems] = useState<HearingItem[]>([]);

  useEffect(() => {
    if (!company) return;
    setSubjectPrefix(company.notificationSubjectPrefix ?? DEFAULT_SUBJECT_PREFIX);
    setBodyTemplate(company.notificationBodyTemplate ?? DEFAULT_BODY_TEMPLATE);
    setReportSystemPrompt(company.reportFormatSystemPrompt ?? DEFAULT_REPORT_SYSTEM);
    setReportUserPrefix(company.reportFormatUserPrefix ?? DEFAULT_REPORT_USER_PREFIX);
  }, [company]);

  useEffect(() => {
    if (!company?.id) return;
    api.companies
      .getHearingItems(company.id)
      .then(res => setHearingItems(res.items))
      .catch(() => setHearingItems([]));
  }, [company?.id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const toNullIfDefault = (v: string, d: string) => {
        const t = v.trim();
        return t === "" || t === d.trim() ? null : t;
      };
      await Promise.all([
        api.companies.updatePromptSettings(company.id, {
          notificationSubjectPrefix: toNullIfDefault(subjectPrefix, DEFAULT_SUBJECT_PREFIX),
          notificationBodyTemplate: toNullIfDefault(bodyTemplate, DEFAULT_BODY_TEMPLATE),
          reportFormatSystemPrompt: toNullIfDefault(reportSystemPrompt, DEFAULT_REPORT_SYSTEM),
          reportFormatUserPrefix: toNullIfDefault(reportUserPrefix, DEFAULT_REPORT_USER_PREFIX),
        }),
        api.companies.updateHearingItems(
          company.id,
          hearingItems.map((item, i) => ({
            label: item.label.trim() || `項目 ${i + 1}`,
            sortOrder: i,
          }))
        ),
      ]);
      const hearingRes = await api.companies.getHearingItems(company.id);
      setHearingItems(hearingRes.items);
      await refresh();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!company) return null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">メッセージ設定</h2>
        <p className="text-muted-foreground text-sm mt-1">
          通知メールと報告書整形の文言・プロンプトを会社ごとに変更できます。表示されているデフォルトをそのまま編集して保存できます。
        </p>
      </div>

      {error && (
        <p className="text-destructive text-sm">{error}</p>
      )}
      {success && (
        <p className="text-green-600 dark:text-green-400 text-sm">保存しました</p>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">通知メール</CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              商談終了後に送るヒアリング依頼メールの件名プレフィックスと本文テンプレート
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subjectPrefix">件名プレフィックス</Label>
              <Input
                id="subjectPrefix"
                value={subjectPrefix}
                onChange={e => setSubjectPrefix(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                例: [商談ヒアリング] → 件名は「プレフィックス + 商談タイトル」
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bodyTemplate">本文テンプレート</Label>
              <Textarea
                id="bodyTemplate"
                value={bodyTemplate}
                onChange={e => setBodyTemplate(e.target.value)}
                rows={8}
                className="font-mono text-sm resize-y"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">ヒアリング項目</CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              通知メール本文の &#123;&#123;hearingSection&#125;&#125; に挿入される項目リスト。並び順は上から反映されます。
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {hearingItems.map((item, index) => (
              <div key={item.id} className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm w-6 shrink-0">{index + 1}.</span>
                <Input
                  value={item.label}
                  onChange={e =>
                    setHearingItems(prev =>
                      prev.map((x, i) => (i === index ? { ...x, label: e.target.value } : x))
                    )
                  }
                  className="font-mono text-sm flex-1"
                  placeholder="例: Budget（予算）"
                />
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    disabled={index === 0}
                    onClick={() =>
                      setHearingItems(prev => {
                        const next = [...prev];
                        const a = next[index - 1];
                        const b = next[index];
                        if (a != null && b != null) {
                          next[index - 1] = b;
                          next[index] = a;
                        }
                        return next;
                      })
                    }
                    aria-label="上へ"
                  >
                    <ChevronUp className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    disabled={index === hearingItems.length - 1}
                    onClick={() =>
                      setHearingItems(prev => {
                        const next = [...prev];
                        const a = next[index];
                        const b = next[index + 1];
                        if (a != null && b != null) {
                          next[index] = b;
                          next[index + 1] = a;
                        }
                        return next;
                      })
                    }
                    aria-label="下へ"
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-destructive hover:text-destructive"
                    disabled={hearingItems.length <= 1}
                    onClick={() => setHearingItems(prev => prev.filter((_, i) => i !== index))}
                    aria-label="削除"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setHearingItems(prev => [
                  ...prev,
                  {
                    id: `new-${Date.now()}`,
                    label: "",
                    sortOrder: prev.length,
                  },
                ])
              }
            >
              <Plus className="size-4" />
              項目を追加
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">報告書整形（LLM）</CardTitle>
            <p className="text-muted-foreground text-sm font-normal">
              返信メモを営業報告書に整形する際の Claude 用プロンプト。
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reportSystemPrompt">System プロンプト</Label>
              <Textarea
                id="reportSystemPrompt"
                value={reportSystemPrompt}
                onChange={e => setReportSystemPrompt(e.target.value)}
                rows={5}
                className="font-mono text-sm resize-y"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reportUserPrefix">User メッセージの先頭文</Label>
              <Textarea
                id="reportUserPrefix"
                value={reportUserPrefix}
                onChange={e => setReportUserPrefix(e.target.value)}
                rows={3}
                className="font-mono text-sm resize-y"
              />
              <p className="text-xs text-muted-foreground">
                その直後に生メモ（rawReport）が連結されて LLM に送られます。
              </p>
            </div>
          </CardContent>
        </Card>

        <Button type="submit" disabled={saving} className="gap-2">
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {saving ? "保存中…" : "保存"}
        </Button>
      </form>
    </div>
  );
}
